<template>
    <section class="monitoring-section">
        <!-- Header Row -->
        <div class="row justify-content-between align-items-center mb-3">
            <div class="col-12 col-md">
                <nav aria-label="breadcrumb">
                    <ul class="breadcrumb mb-0">
                        <li class="breadcrumb-item h5 h3-md font-weight-bold mb-0">
                            <router-link v-bind:to="{ name: 'tryout/online/index' }">
                                Tryout
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active font-weight-medium" aria-current="page">
                            Monitoring
                        </li>
                    </ul>
                </nav>
            </div>
            <div class="col-12 col-md-auto mt-2 mt-md-0">
                <button type="button" class="btn btn-danger btn-sm font-weight-medium w-100 w-md-auto" v-on:click="refreshData">
                    <i class="fa fa-refresh" v-bind:class="{'fa-spin': loading}"></i> Refresh
                </button>
            </div>
        </div>

        <!-- Summary Cards -->
        <div class="row mb-3 mb-md-4">
            <div class="col-4">
                <div class="card shadow-sm border-0 summary-card">
                    <div class="card-body text-center py-2 py-md-3">
                        <h3 class="mb-0 font-weight-bold text-primary summary-number">{{ summary.totalAttempts }}</h3>
                        <p class="text-muted mb-0 summary-label">Total</p>
                    </div>
                </div>
            </div>
            <div class="col-4">
                <div class="card shadow-sm border-0 summary-card">
                    <div class="card-body text-center py-2 py-md-3">
                        <h3 class="mb-0 font-weight-bold text-warning summary-number">{{ summary.inProgress }}</h3>
                        <p class="text-muted mb-0 summary-label">Progress</p>
                    </div>
                </div>
            </div>
            <div class="col-4">
                <div class="card shadow-sm border-0 summary-card">
                    <div class="card-body text-center py-2 py-md-3">
                        <h3 class="mb-0 font-weight-bold text-success summary-number">{{ summary.submitted }}</h3>
                        <p class="text-muted mb-0 summary-label">Finished</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Filters -->
        <div class="row mb-3 mb-md-4">
            <div class="col-12">
                <div class="card shadow-sm border-0">
                    <div class="card-header background-primary py-2 py-md-3">
                        <div class="row align-items-center">
                            <div class="col">
                                <span class="h6 h5-md font-weight-medium text-white mb-0">
                                    <i class="fa fa-filter"></i> Filter
                                </span>
                            </div>
                            <div class="col-auto d-md-none">
                                <button class="btn btn-sm btn-light" type="button" v-on:click="showFilters = !showFilters">
                                    <i class="fa" v-bind:class="showFilters ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body py-2 py-md-3" v-bind:class="{'d-none d-md-block': !showFilters}">
                        <!-- Period Filter Row -->
                        <div class="row mb-2 mb-md-3">
                            <div class="col-12 col-md-6 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">
                                    <i class="fa fa-calendar"></i> Start Period
                                </label>
                                <input 
                                    type="datetime-local" 
                                    v-model="filter.startDate" 
                                    class="form-control form-control-sm"
                                    step="1"
                                />
                            </div>
                            <div class="col-12 col-md-6 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">
                                    <i class="fa fa-calendar"></i> End Period
                                </label>
                                <input 
                                    type="datetime-local" 
                                    v-model="filter.endDate" 
                                    class="form-control form-control-sm"
                                    step="1"
                                />
                            </div>
                        </div>
                        <!-- Main Filters Row -->
                        <div class="row">
                            <div class="col-6 col-md-2 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">Tryout</label>
                                <select v-model="filter.tryoutID" class="form-control form-control-sm">
                                    <option value="">Semua</option>
                                    <option v-for="tryout in filterOptions.tryouts" v-bind:value="tryout.id" v-bind:key="tryout.id">
                                        {{ tryout.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-6 col-md-2 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">Cabang</label>
                                <select v-model="filter.branchID" class="form-control form-control-sm" v-bind:disabled="isBranchRestricted">
                                    <option value="" v-if="!isBranchRestricted">Semua</option>
                                    <option v-for="branch in filterOptions.branches" v-bind:value="branch.id" v-bind:key="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-6 col-md-2 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">Kelas</label>
                                <select v-model="filter.gradeID" class="form-control form-control-sm">
                                    <option value="">Semua</option>
                                    <option v-for="grade in filterOptions.grades" v-bind:value="grade.id" v-bind:key="grade.id">
                                        {{ grade.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-6 col-md-2 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">Mapel</label>
                                <select v-model="filter.subjectID" class="form-control form-control-sm">
                                    <option value="">Semua</option>
                                    <option v-for="subject in filterOptions.subjects" v-bind:value="subject.id" v-bind:key="subject.id">
                                        {{ subject.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-12 col-md-4 mb-2">
                                <label class="font-weight-medium text-muted mb-1 small">Cari Nama</label>
                                <input v-model="search" type="text" class="form-control form-control-sm" placeholder="Cari..." v-on:keyup.enter="applyFilters" />
                            </div>
                        </div>
                        <!-- Apply Filter Button -->
                        <div class="row mt-2 mt-md-3">
                            <div class="col-12 text-right">
                                <button class="btn btn-outline-secondary btn-sm mr-2" type="button" v-on:click="resetFilters">
                                    <i class="fa fa-times"></i> <span class="d-none d-md-inline">Reset</span>
                                </button>
                                <button class="btn btn-danger btn-sm" type="button" v-on:click="applyFilters">
                                    <i class="fa fa-search"></i> <span class="d-none d-md-inline">Terapkan Filter</span>
                                    <span class="d-md-none">Filter</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bar Chart -->
        <div class="row mb-3 mb-md-4">
            <div class="col-12">
                <div class="card shadow-sm border-0">
                    <div class="card-header background-primary py-2 py-md-3">
                        <div class="row align-items-center">
                            <div class="col">
                                <span class="h6 h5-md font-weight-medium text-white mb-0">
                                    <i class="fa fa-bar-chart"></i> <span class="d-none d-sm-inline">Grafik Aktivitas</span><span class="d-sm-none">Grafik</span>
                                </span>
                            </div>
                            <div class="col-auto d-flex align-items-center">
                                <select v-model="chartGroupBy" class="form-control form-control-sm chart-groupby-select mr-2" v-on:change="fetchChartData">
                                    <option value="hour">Per Jam</option>
                                    <option value="daily">Per Hari</option>
                                </select>
                                <span class="text-white small d-none d-lg-inline">
                                    {{ periodLabel }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card-body py-2 py-md-3">
                        <div class="chart-container">
                            <IEcharts v-bind:option="chartOption" resizable />
                        </div>
                        <div class="row justify-content-center mt-2 mt-md-3">
                            <div class="col-auto">
                                <span class="badge badge-warning mr-2 mr-md-3 badge-mobile">
                                    <i class="fa fa-circle"></i> Progress
                                </span>
                                <span class="badge badge-success badge-mobile">
                                    <i class="fa fa-circle"></i> Finished
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div class="row">
            <div class="col-12">
                <div class="card shadow-sm border-0">
                    <div class="card-header background-primary py-2 py-md-3">
                        <div class="row align-items-center">
                            <div class="col">
                                <span class="h6 h5-md font-weight-medium text-white mb-0">
                                    <i class="fa fa-list"></i> <span class="d-none d-sm-inline">Daftar Siswa</span><span class="d-sm-none">Data</span>
                                </span>
                            </div>
                            <div class="col-auto">
                                <span class="text-white small">
                                    {{ tableMeta.total }} data
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-0">
                        <!-- Mobile Card View -->
                        <div class="d-md-none">
                            <div v-if="loading" class="text-center py-4">
                                <i class="fa fa-spinner fa-spin fa-2x"></i>
                                <p class="mb-0 mt-2">Memuat data...</p>
                            </div>
                            <div v-else-if="!tableData.length" class="text-center py-4">
                                <i class="fa fa-inbox fa-2x text-muted"></i>
                                <p class="mb-0 mt-2 text-muted">Tidak ada data</p>
                            </div>
                            <div v-else class="mobile-card-list">
                                <div v-for="(item, index) in tableData" v-bind:key="item.studentID + '-' + item.tryoutSubjectID + '-mobile'" class="mobile-card">
                                    <div class="mobile-card-header">
                                        <span class="mobile-card-number">#{{ (tableMeta.page - 1) * tableMeta.limit + index + 1 }}</span>
                                        <div class="d-flex align-items-center">
                                            <span v-if="item.isSubmitted" class="badge badge-success badge-sm mr-2">
                                                <i class="fa fa-check"></i> Finished
                                            </span>
                                            <span v-else class="badge badge-warning badge-sm mr-2">
                                                <i class="fa fa-clock-o"></i> Progress
                                            </span>
                                            <button 
                                                type="button" 
                                                class="btn btn-outline-danger btn-sm py-0 px-2" 
                                                @click="openResetModal(item)" 
                                                title="Reset pengerjaan siswa tanpa menghapus jawaban">
                                                <i class="fa fa-undo"></i> Reset
                                            </button>
                                        </div>
                                    </div>
                                    <div class="mobile-card-body">
                                        <div class="mobile-card-name">{{ item.userName }}</div>
                                        <div class="mobile-card-info">
                                            <span><i class="fa fa-id-card-o"></i> {{ item.identity }}</span>
                                        </div>
                                        <div class="mobile-card-details">
                                            <span><i class="fa fa-building"></i> {{ item.branchName }}</span>
                                            <span><i class="fa fa-graduation-cap"></i> {{ item.gradeName }}</span>
                                        </div>
                                        <div class="mobile-card-details">
                                            <span><i class="fa fa-book"></i> {{ item.subjectName }}</span>
                                        </div>
                                        <div class="mobile-card-details">
                                            <span><i class="fa fa-file-text-o"></i> {{ item.tryoutName }}</span>
                                        </div>
                                        <div class="mobile-card-details text-muted">
                                            <span><i class="fa fa-clock-o"></i> Mulai: {{ formatDateTime(item.start) }}</span>
                                        </div>
                                        <div class="mobile-card-details text-muted">
                                            <span><i class="fa fa-check-circle-o"></i> Selesai: {{ item.isSubmitted && item.finishTime ? formatDateTime(item.finishTime) : '-' }}</span>
                                        </div>
                                        <div class="mobile-card-details text-muted">
                                            <span><i class="fa fa-hourglass-half"></i> Total Waktu: {{ formatDuration(item) }}</span>
                                        </div>
                                        <div class="mobile-card-details">
                                            <span>
                                                <i class="fa fa-exclamation-triangle"></i> Peringatan Keluar: 
                                                <strong v-if="item.warningCount > 0" class="badge badge-danger badge-sm ml-1">{{ item.warningCount }}x</strong>
                                                <span v-else class="text-muted ml-1">0x</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Desktop Table View -->
                        <div class="table-responsive d-none d-md-block">
                            <table class="table table-hover table-striped mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th class="text-center" style="width: 50px;">#</th>
                                        <th>Nama Siswa</th>
                                        <th>Identity</th>
                                        <th>Cabang</th>
                                        <th>Kelas</th>
                                        <th>Tryout</th>
                                        <th>Mata Pelajaran</th>
                                        <th>Waktu Mulai</th>
                                        <th>Waktu Selesai</th>
                                        <th>Total Waktu</th>
                                        <th class="text-center">Peringatan Keluar</th>
                                        <th class="text-center">Status</th>
                                        <th class="text-center" style="width: 100px;">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="loading">
                                        <td colspan="13" class="text-center py-4">
                                            <i class="fa fa-spinner fa-spin fa-2x"></i>
                                            <p class="mb-0 mt-2">Memuat data...</p>
                                        </td>
                                    </tr>
                                    <tr v-else-if="!tableData.length">
                                        <td colspan="13" class="text-center py-4">
                                            <i class="fa fa-inbox fa-2x text-muted"></i>
                                            <p class="mb-0 mt-2 text-muted">Tidak ada data</p>
                                        </td>
                                    </tr>
                                    <tr v-for="(item, index) in tableData" v-bind:key="item.studentID + '-' + item.tryoutSubjectID">
                                        <td class="text-center">{{ (tableMeta.page - 1) * tableMeta.limit + index + 1 }}</td>
                                        <td class="font-weight-medium">{{ item.userName }}</td>
                                        <td>{{ item.identity }}</td>
                                        <td>{{ item.branchName }}</td>
                                        <td>{{ item.gradeName }}</td>
                                        <td>{{ item.tryoutName }}</td>
                                        <td>{{ item.subjectName }}</td>
                                        <td>{{ formatDateTime(item.start) }}</td>
                                        <td>
                                            <span v-if="item.isSubmitted && item.finishTime">
                                                {{ formatDateTime(item.finishTime) }}
                                            </span>
                                            <span v-else class="text-muted font-italic">
                                                -
                                            </span>
                                        </td>
                                        <td>
                                            <span v-if="formatDuration(item) !== '-'" class="font-weight-medium">
                                                <i class="fa fa-hourglass-half text-muted mr-1"></i>{{ formatDuration(item) }}
                                            </span>
                                            <span v-else class="text-muted font-italic">
                                                -
                                            </span>
                                        </td>
                                        <td class="text-center">
                                            <span v-if="item.warningCount > 0" class="badge badge-danger font-weight-bold px-2 py-1" v-b-tooltip.hover title="Terdeteksi keluar dari layar pengerjaan soal">
                                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ item.warningCount }}x
                                            </span>
                                            <span v-else class="badge badge-light text-muted border px-2 py-1">
                                                0x
                                            </span>
                                        </td>
                                        <td class="text-center">
                                            <span v-if="item.isSubmitted" class="badge badge-success">
                                                <i class="fa fa-check"></i> Finished
                                            </span>
                                            <span v-else class="badge badge-warning">
                                                <i class="fa fa-clock-o"></i> Progress
                                            </span>
                                        </td>
                                        <td class="text-center">
                                            <button 
                                                type="button" 
                                                class="btn btn-outline-danger btn-sm" 
                                                @click="openResetModal(item)" 
                                                title="Reset pengerjaan siswa tanpa menghapus jawaban">
                                                <i class="fa fa-undo mr-1"></i> Reset
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="card-footer py-2 py-md-3">
                        <div class="row align-items-center">
                            <div class="col-12 col-md text-center text-md-left mb-2 mb-md-0">
                                <small class="text-muted">
                                    {{ tableData.length }} dari {{ tableMeta.total }}
                                </small>
                            </div>
                            <div class="col-12 col-md-auto">
                                <nav class="d-flex justify-content-center justify-content-md-end">
                                    <paginate 
                                        v-model="tableMeta.page" 
                                        next-text="<i class='fa fa-chevron-right'></i>" 
                                        prev-text="<i class='fa fa-chevron-left'></i>" 
                                        v-bind:click-handler="paginate" 
                                        v-bind:page-count="tableMeta.totalPages" 
                                        container-class="pagination pagination-sm mb-0 flex-wrap justify-content-center" 
                                        page-class="page-item" 
                                        prev-class="page-item" 
                                        next-class="page-item" 
                                        page-link-class="page-link" 
                                        prev-link-class="page-link" 
                                        next-link-class="page-link" 
                                        v-bind:hide-prev-next="true">
                                    </paginate>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Konfirmasi Reset Pengerjaan Siswa -->
        <b-modal v-model="modal.reset" modal-class="border-0" header-class="bg-danger text-white" v-bind:centered="true" v-bind:hide-footer="true">
            <template slot="modal-header">
                <h5 class="modal-title font-weight-bold text-white mb-0">
                    <i class="fa fa-undo mr-1"></i> Reset Pengerjaan Siswa
                </h5>
            </template>
            <div class="py-2" v-if="selectedItem">
                <p class="text-secondary mb-3">
                    Apakah Anda yakin ingin mereset pengerjaan untuk siswa berikut?
                </p>

                <div class="bg-light p-3 rounded mb-3 border">
                    <div class="row mb-1">
                        <div class="col-4 text-muted font-weight-bold">Nama:</div>
                        <div class="col-8 font-weight-medium">{{ selectedItem.userName }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-4 text-muted font-weight-bold">Identity:</div>
                        <div class="col-8">{{ selectedItem.identity }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-4 text-muted font-weight-bold">Mata Pelajaran:</div>
                        <div class="col-8 font-weight-medium">{{ selectedItem.subjectName }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-4 text-muted font-weight-bold">Tryout:</div>
                        <div class="col-8">{{ selectedItem.tryoutName }}</div>
                    </div>
                    <div class="row">
                        <div class="col-4 text-muted font-weight-bold">Status:</div>
                        <div class="col-8">
                            <span v-if="selectedItem.isSubmitted" class="badge badge-success">Finished</span>
                            <span v-else class="badge badge-warning">Progress</span>
                        </div>
                    </div>
                </div>

                <div class="alert alert-info py-2 px-3 small mb-4">
                    <i class="fa fa-info-circle mr-1"></i>
                    <strong>Catatan:</strong> Tindakan ini akan menghapus data pengerjaan (attempt) agar siswa dapat mengulang/melanjutkan pengerjaan. 
                    <strong>Jawaban siswa yang sudah tersimpan TIDAK AKAN DIHAPUS</strong>. Kunci sesi pengawas dan hitungan peringatan keluar juga akan dibersihkan.
                </div>

                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary btn-sm px-3 mr-2" @click="modal.reset = false" :disabled="isResetting">
                        Batal
                    </button>
                    <button type="button" class="btn btn-danger btn-sm px-3" @click="executeResetAttempt" :disabled="isResetting">
                        <i v-if="isResetting" class="fa fa-spinner fa-spin mr-1"></i>
                        <i v-else class="fa fa-undo mr-1"></i>
                        Ya, Reset Pengerjaan
                    </button>
                </div>
            </div>
        </b-modal>
    </section>
</template>

<script>
    import { mapState } from 'vuex';
    import IEcharts from 'vue-echarts-v3/src/full.js';
    import Paginate from 'vuejs-paginate';

    export default {
        name: "TryoutMonitoring",
        components: {
            IEcharts,
            Paginate
        },
        computed: {
            ...mapState([
                'profile',
                'term'
            ]),
            periodLabel() {
                if (this.filter.startDate && this.filter.endDate) {
                    return this.formatDateTime(this.filter.startDate) + ' - ' + this.formatDateTime(this.filter.endDate);
                }
                return 'Data 24 Jam Terakhir';
            },
            userRestrictedBranchID() {
                if (this.profile && !this.profile.isAdmin && this.profile.isEmployee && this.profile.employee) {
                    if (this.profile.employee.isDirector || this.profile.employee.isManager) {
                        return null;
                    }
                    if (this.profile.employee.isBranchStaff && this.profile.employee.branchStaff) {
                        return this.profile.employee.branchStaff.branchID;
                    }
                    if (this.profile.employee.isBranchHead && this.profile.employee.branchHead) {
                        return this.profile.employee.branchHead.branchID;
                    }
                    if (this.profile.employee.isMentor && this.profile.employee.mentor) {
                        return this.profile.employee.mentor.branchID;
                    }
                }
                return null;
            },
            isBranchRestricted() {
                return Boolean(this.userRestrictedBranchID) || (!this.profile.isAdmin && this.filterOptions.branches && this.filterOptions.branches.length === 1);
            }
        },
        watch: {
            term() {
                this.refreshData();
            }
        },
        data() {
            return {
                loading: false,
                showFilters: false,
                search: '',
                modal: {
                    reset: false
                },
                selectedItem: null,
                isResetting: false,
                chartGroupBy: 'hour', // 'hour' or 'daily'
                filter: {
                    branchID: '',
                    gradeID: '',
                    subjectID: '',
                    tryoutID: '',
                    startDate: '',
                    endDate: ''
                },
                filterOptions: {
                    branches: [],
                    grades: [],
                    subjects: [],
                    tryouts: []
                },
                summary: {
                    totalAttempts: 0,
                    inProgress: 0,
                    submitted: 0
                },
                chartData: [],
                tableData: [],
                tableMeta: {
                    total: 0,
                    page: 1,
                    limit: 20,
                    totalPages: 0
                },
                chartOption: {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: {
                            type: 'shadow'
                        }
                    },
                    legend: {
                        data: ['Progress', 'Finished'],
                        bottom: 0,
                        textStyle: {
                            fontSize: 11
                        }
                    },
                    grid: {
                        left: '3%',
                        right: '4%',
                        bottom: '18%',
                        top: '10%',
                        containLabel: true
                    },
                    xAxis: {
                        type: 'category',
                        data: [],
                        axisLabel: {
                            rotate: 45,
                            interval: 0,
                            fontSize: 9
                        }
                    },
                    yAxis: {
                        type: 'value',
                        minInterval: 1
                    },
                    series: [
                        {
                            name: 'Progress',
                            type: 'bar',
                            stack: 'total',
                            barWidth: '60%',
                            itemStyle: {
                                color: '#FFC107'
                            },
                            data: []
                        },
                        {
                            name: 'Finished',
                            type: 'bar',
                            stack: 'total',
                            barWidth: '60%',
                            itemStyle: {
                                color: '#28A745'
                            },
                            data: []
                        }
                    ]
                }
            };
        },
        methods: {
            formatDateTime(dateString) {
                if (!dateString) return '';
                return this.$moment(dateString).format('DD MMM YYYY HH:mm:ss');
            },
            formatDuration(item) {
                if (!item) return '-';
                if (item.durationText) return item.durationText;

                if (!item.start) return '-';
                let start = this.$moment(item.start);
                if (!start.isValid()) return '-';

                let end = null;
                if (item.isSubmitted) {
                    let fTime = item.finishTime || item.updatedAt;
                    if (fTime) {
                        end = this.$moment(fTime);
                    }
                } else {
                    end = this.$moment();
                }

                if (!end || !end.isValid()) return '-';

                let diffMs = end.diff(start);
                if (diffMs < 0) diffMs = 0;

                let totalSeconds = Math.floor(diffMs / 1000);
                let hours = Math.floor(totalSeconds / 3600);
                let minutes = Math.floor((totalSeconds % 3600) / 60);
                let seconds = totalSeconds % 60;

                let parts = [];
                if (hours > 0) parts.push(hours + ' Jam');
                if (minutes > 0) parts.push(minutes + ' Menit');
                if (seconds > 0 || parts.length === 0) parts.push(seconds + ' Detik');

                let text = parts.join(' ');
                return item.isSubmitted ? text : text + ' (Berjalan)';
            },
            formatPeriodLabel(periodString, groupBy) {
                if (groupBy === 'daily') {
                    return this.$moment(periodString).format('DD MMM');
                }
                return this.$moment(periodString).format('DD/MM HH:mm');
            },
            getDefault24HoursRange() {
                const now = this.$moment();
                const startDate = this.$moment().subtract(24, 'hours');
                return {
                    startDate: startDate.format('YYYY-MM-DDTHH:mm:ss'),
                    endDate: now.format('YYYY-MM-DDTHH:mm:ss')
                };
            },
            initializeDefaultDates() {
                const range = this.getDefault24HoursRange();
                this.filter.startDate = range.startDate;
                this.filter.endDate = range.endDate;
            },
            resetFilters() {
                let defaultBranchID = this.userRestrictedBranchID || (this.filterOptions.branches && this.filterOptions.branches.length === 1 && !this.profile.isAdmin ? this.filterOptions.branches[0].id : '');
                this.filter.branchID = defaultBranchID;
                this.filter.gradeID = '';
                this.filter.subjectID = '';
                this.filter.tryoutID = '';
                this.search = '';
                this.initializeDefaultDates();
                this.applyFilters();
            },
            refreshData() {
                this.fetchChartData();
                this.fetchTableData();
                this.fetchSummary();
            },
            applyFilters() {
                this.tableMeta.page = 1;
                this.showFilters = false;
                // Clear tryout and subject selection when period changes
                // as the options will be refreshed based on new period
                this.fetchFilterOptions();
                this.refreshData();
            },
            getFilterParams() {
                let params = {
                    termID: this.term.id
                };

                if (this.filter.startDate) {
                    params.startDate = this.$moment(this.filter.startDate).format('YYYY-MM-DD HH:mm:ss');
                }
                if (this.filter.endDate) {
                    params.endDate = this.$moment(this.filter.endDate).format('YYYY-MM-DD HH:mm:ss');
                }

                if (this.filter.branchID) {
                    params['filter[branchID]'] = this.filter.branchID;
                }
                if (this.filter.gradeID) {
                    params['filter[gradeID]'] = this.filter.gradeID;
                }
                if (this.filter.subjectID) {
                    params['filter[subjectID]'] = this.filter.subjectID;
                }
                if (this.filter.tryoutID) {
                    params['filter[tryoutID]'] = this.filter.tryoutID;
                }

                return params;
            },
            fetchFilterOptions() {
                let params = {
                    termID: this.term.id
                };
                if (this.filter.startDate) {
                    params.startDate = this.$moment(this.filter.startDate).format('YYYY-MM-DD HH:mm:ss');
                }
                if (this.filter.endDate) {
                    params.endDate = this.$moment(this.filter.endDate).format('YYYY-MM-DD HH:mm:ss');
                }

                this.axios.get(process.env.API_URL + '/monitoring/filter-options', { params }).then(response => {
                    if (response.data.status) {
                        this.filterOptions = response.data.data;
                        
                        // Kunci pemilihan cabang jika pengguna memiliki cabang khusus
                        if (this.userRestrictedBranchID) {
                            this.filter.branchID = this.userRestrictedBranchID;
                        } else if (this.filterOptions.branches && this.filterOptions.branches.length === 1 && !this.profile.isAdmin) {
                            this.filter.branchID = this.filterOptions.branches[0].id;
                        }

                        // Reset tryout/subject selection if they don't exist in new options
                        if (this.filter.tryoutID && !this.filterOptions.tryouts.find(t => t.id == this.filter.tryoutID)) {
                            this.filter.tryoutID = '';
                        }
                        if (this.filter.subjectID && !this.filterOptions.subjects.find(s => s.id == this.filter.subjectID)) {
                            this.filter.subjectID = '';
                        }
                    }
                });
            },
            fetchChartData() {
                let params = {
                    ...this.getFilterParams(),
                    groupBy: this.chartGroupBy,
                    search: this.search
                };
                
                this.axios.get(process.env.API_URL + '/monitoring/chart', { params }).then(response => {
                    if (response.data.status) {
                        this.chartData = response.data.data;
                        this.updateChart(response.data.groupBy || this.chartGroupBy);
                    }
                });
            },
            fetchTableData() {
                this.loading = true;
                let params = {
                    ...this.getFilterParams(),
                    page: this.tableMeta.page,
                    limit: this.tableMeta.limit,
                    search: this.search
                };
                
                this.axios.get(process.env.API_URL + '/monitoring/table', { params }).then(response => {
                    this.loading = false;
                    if (response.data.status) {
                        this.tableData = response.data.data;
                        this.tableMeta = response.data.meta;
                    }
                }).catch(() => {
                    this.loading = false;
                });
            },
            fetchSummary() {
                let params = {
                    ...this.getFilterParams(),
                    search: this.search
                };
                
                this.axios.get(process.env.API_URL + '/monitoring/summary', { params }).then(response => {
                    if (response.data.status) {
                        this.summary = response.data.data;
                    }
                });
            },
            updateChart(groupBy) {
                let labels = [];
                let progressData = [];
                let finishedData = [];

                this.chartData.forEach(item => {
                    labels.push(this.formatPeriodLabel(item.period, groupBy));
                    progressData.push(item.inProgress);
                    finishedData.push(item.submitted);
                });

                // Adjust axis label styling based on data length
                let axisLabelConfig = {
                    rotate: labels.length > 15 ? 45 : 0,
                    interval: labels.length > 30 ? Math.floor(labels.length / 20) : 0,
                    fontSize: labels.length > 20 ? 8 : 9
                };

                this.chartOption = {
                    ...this.chartOption,
                    xAxis: {
                        ...this.chartOption.xAxis,
                        data: labels,
                        axisLabel: axisLabelConfig
                    },
                    series: [
                        {
                            ...this.chartOption.series[0],
                            data: progressData
                        },
                        {
                            ...this.chartOption.series[1],
                            data: finishedData
                        }
                    ]
                };
            },
            paginate(page) {
                this.tableMeta.page = page;
                this.fetchTableData();
            },
            openResetModal(item) {
                this.selectedItem = item;
                this.modal.reset = true;
            },
            executeResetAttempt() {
                if (!this.selectedItem || this.isResetting) return;
                this.isResetting = true;

                let params = {
                    action: 'reset',
                    studentID: this.selectedItem.studentID,
                    tryoutSubjectID: this.selectedItem.tryoutSubjectID
                };

                let config = {
                    params: params,
                    meta: {
                        throwError: false
                    }
                };

                // Panggil endpoint /monitoring/table?action=reset yang dapat diakses aman oleh Admin, Staf Lokasi, dan Binglas
                this.axios.get(process.env.API_URL + '/monitoring/table', config)
                    .then(response => {
                        this.isResetting = false;
                        if (response.data && response.data.status) {
                            this.modal.reset = false;
                            this.selectedItem = null;
                            this.$toastr('success', response.data.message || 'Pengerjaan siswa berhasil direset. Jawaban tetap tersimpan.', 'Berhasil');
                            this.fetchTableData();
                            this.fetchSummary();
                            this.fetchChartData();
                        } else {
                            let errMsg = (response.data && response.data.message) 
                                || (response.data && response.data.errors ? Object.values(response.data.errors)[0][0] : 'Gagal mereset pengerjaan');
                            this.$toastr('warning', errMsg, 'Warning');
                        }
                    })
                    .catch(err => {
                        this.isResetting = false;
                        let errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Terjadi kesalahan saat mereset pengerjaan';
                        this.$toastr('error', errMsg, 'Error');
                    });
            }
        },
        created() {
            this.initializeDefaultDates();
            if (this.userRestrictedBranchID) {
                this.filter.branchID = this.userRestrictedBranchID;
            }
            this.fetchFilterOptions();
            this.refreshData();
        },
        mounted() {
            document.title = 'Monitoring Tryout - ' + process.env.APP_NAME;
        }
    };
</script>

<style scoped>
    .monitoring-section {
        padding-bottom: 20px;
    }

    /* Chart */
    .chart-container {
        width: 100%;
        height: 250px;
    }

    @media (min-width: 768px) {
        .chart-container {
            height: 350px;
        }
    }

    /* Chart GroupBy Dropdown */
    .chart-groupby-select {
        background-color: rgba(255, 255, 255, 0.9);
        border: none;
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
        min-width: 80px;
        cursor: pointer;
    }

    .chart-groupby-select:focus {
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
    }

    @media (min-width: 768px) {
        .chart-groupby-select {
            font-size: 0.8rem;
            min-width: 100px;
        }
    }
    
    /* Badges */
    .badge-warning {
        background-color: #FFC107;
        color: #212529;
    }
    
    .badge-success {
        background-color: #28A745;
        color: #fff;
    }

    .badge-mobile {
        font-size: 0.7rem;
        padding: 0.3em 0.5em;
    }

    @media (min-width: 768px) {
        .badge-mobile {
            font-size: 0.75rem;
            padding: 0.35em 0.65em;
        }
    }

    .badge-sm {
        font-size: 0.65rem;
        padding: 0.25em 0.4em;
    }
    
    /* Table */
    .table th {
        border-top: none;
        font-weight: 600;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
    }
    
    .table td {
        vertical-align: middle;
        font-size: 0.85rem;
    }

    /* Cards */
    .card {
        border-radius: 8px;
        overflow: hidden;
    }

    .card-header.background-primary {
        background: linear-gradient(to right, #d31027, #ea384d);
    }

    .text-primary {
        color: #d31027 !important;
    }

    /* Summary Cards */
    .summary-card .card-body {
        padding: 0.5rem;
    }

    .summary-number {
        font-size: 1.25rem;
    }

    .summary-label {
        font-size: 0.7rem;
    }

    @media (min-width: 768px) {
        .summary-card .card-body {
            padding: 1rem;
        }

        .summary-number {
            font-size: 2rem;
        }

        .summary-label {
            font-size: 0.9rem;
        }
    }

    /* Datetime input styling */
    input[type="datetime-local"] {
        padding: 0.375rem 0.5rem;
        font-size: 0.8rem;
    }

    @media (min-width: 768px) {
        input[type="datetime-local"] {
            font-size: 0.875rem;
        }
    }

    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
        cursor: pointer;
    }

    /* Mobile Card View */
    .mobile-card-list {
        padding: 0.5rem;
    }

    .mobile-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        margin-bottom: 0.5rem;
        overflow: hidden;
    }

    .mobile-card:last-child {
        margin-bottom: 0;
    }

    .mobile-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0.75rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
    }

    .mobile-card-number {
        font-weight: 600;
        color: #6c757d;
        font-size: 0.75rem;
    }

    .mobile-card-body {
        padding: 0.75rem;
    }

    .mobile-card-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: #333;
        margin-bottom: 0.25rem;
    }

    .mobile-card-info {
        font-size: 0.75rem;
        color: #6c757d;
        margin-bottom: 0.25rem;
    }

    .mobile-card-details {
        font-size: 0.75rem;
        color: #6c757d;
        margin-bottom: 0.25rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
    }

    .mobile-card-details i,
    .mobile-card-info i {
        width: 14px;
        margin-right: 4px;
    }

    .mobile-card-time {
        font-size: 0.7rem;
        color: #999;
        margin-top: 0.5rem;
        padding-top: 0.5rem;
        border-top: 1px dashed #e9ecef;
    }

    /* Responsive utilities */
    @media (max-width: 767.98px) {
        .breadcrumb-item.h3 {
            font-size: 1.1rem !important;
        }

        .breadcrumb-item.active {
            font-size: 0.85rem;
        }

        .h6.h5-md {
            font-size: 0.9rem;
        }

        .btn-sm {
            font-size: 0.75rem;
            padding: 0.3rem 0.6rem;
        }

        .form-control-sm {
            font-size: 0.8rem;
        }

        select.form-control-sm {
            padding-right: 1.5rem;
        }

        label.small {
            font-size: 0.7rem;
        }

        .pagination {
            margin: 0;
        }

        .page-link {
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
        }
    }

    @media (min-width: 768px) {
        .h3-md {
            font-size: 1.75rem !important;
        }

        .h5-md {
            font-size: 1.25rem;
        }

        .w-md-auto {
            width: auto !important;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/tryout/monitoring/Index.vue